// Chakra imports
import {
  Flex,
  Button,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Box,
} from "@chakra-ui/react";
import { brokerSonnerToast } from "../../../utils/brokerSonnerToast";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useLocation, useHistory } from "react-router-dom";
// Custom components
import TradeLogHeader from "./components/TradeLogHeader";
import { useShadcnInsetChrome } from "@/contexts/ShadcnInsetChromeContext";
import TradesTable from "./components/TradesTable";
import TradeDetailsInlinePanel, {
  type TradeDetailsInlinePanelHandle,
} from "./components/TradeDetailsInlinePanel";
import LogTradeModal from "./components/LogTradeModal";
import TemplateManagementModal from "./components/TemplateManagementModal";
import {
  StatisticsEmptyState,
  statisticsPageMinHeight,
} from "../Statistics/components/statisticsEmptyState";
import ErrorBoundary from "../../../components/ErrorBoundary/ErrorBoundary";
import TradeReviewTab from "./components/TradeReview/TradeReviewTab";
import { InstrumentSetupGate } from "../../../components/Instruments/InstrumentSetupGate";
// Services
// Mock data removed - all data comes from backend
import { backendTradeToFrontendTrade, frontendTradeToBackendTrade } from "../../../services/tradeStorage";
import { createTrade } from "../../../services/tradeApi";
import { fetchTrades } from "../../../services/tradeApi";
import { calculateTradeValues, calculateTradeStatistics } from "../../../utils/tradeCalculations";
import { useTradeStatistics, clearAllTradeCaches, invalidateTradeDataCaches, isDestructiveTradeDataAction, useTradeDataFreshMountInvalidation } from "../../../hooks/useTradeStatistics";
import { useSignalsContextOptional } from "../../../contexts/SignalsContext";
import { useLoadActiveBrokerSnapshot } from "../../../hooks/useLoadActiveBrokerSnapshot";
import { DateRange, Trade, TradeFilters, TradeFormData, TradeStatisticsFilters } from "../../../types/trade";
import { tradeConflictsWithBrokerAccount } from "../../../utils/brokerAccountScope";
import { resolveTradeListWindowQuery } from "../../../utils/notificationPeriod";
import { useUserNotificationTimezone } from "../../../hooks/useUserNotificationTimezone";
import { useActiveBrokerAccount } from "../../../hooks/useActiveBrokerAccount";
import {
  buildProfileBrokerApiUrl,
  resolveProfileBrokerTabForImport,
} from "../../../utils/profileBrokerNavigation";
import { exportTradesToCSV, generateExportFilename } from "../../../utils/csvExport";
import { Attachment } from "../../../types/attachment";
import { useDashboardDateWindowListener } from "../../../hooks/useDashboardDateWindowListener";
import {
  dateRangeForTradeLogFromPersisted,
  deriveTimeframeKeyFromLocalDateRange,
  equalDateRange,
  getInitialDashboardDateWindow,
  persistStateFromTradeLog,
  preferredPresetFromPersisted,
  writeDashboardDateWindow,
  type DashboardDatePresetTimeframe,
  type DashboardDateRangeChangeOptions,
} from "../../../utils/dashboardDateWindowStorage";
import { normalizeDateRangeForApi, normalizeLocalDateRangeForApi } from "../../../utils/dateUtils";
import { useUser } from "@clerk/clerk-react";
import { formDataToTrade } from "../../../utils/tradeFormData";
import { deleteAttachmentsForTrade, migrateAttachmentsTradeId } from "../../../services/attachmentService";
import { safeRandomUUID } from "../../../utils/safeRandomUUID";

const FlexAny = Flex as any;
const AlertAny = Alert as any;
const AlertTitleAny = AlertTitle as any;
const AlertDescriptionAny = AlertDescription as any;
const ButtonAny = Button as any;

type TradeLogTab = "log" | "review";
const TradeLogHeaderAny = TradeLogHeader as React.ComponentType<any>;

/** Stable key for GET /trades list (+ optional open merge). Skips remount/settle refetches. */
function buildTradeLogListQueryKey(args: {
  userId: string | undefined;
  accountCode: string | null | undefined;
  brokerName: string | null | undefined;
  dateRange: DateRange | undefined;
  status: TradeFilters["status"] | undefined;
  timeframe: string;
  reportingTimeZone: string;
}): string {
  const win = resolveTradeListWindowQuery(
    args.dateRange || { startDate: null, endDate: null },
    args.timeframe,
    args.reportingTimeZone,
    { includeTimezoneQuery: true }
  );
  const windowPart =
    win.kind === "local"
      ? `local:${win.period.local_start_date}:${win.period.local_end_date}:${win.period.timezone ?? ""}`
      : `tf:${win.timeframe}`;
  const includeOpen =
    !args.status || args.status === "all" || args.status === "open" ? "1" : "0";
  return [
    args.userId ?? "",
    args.accountCode ?? "",
    args.brokerName ?? "",
    args.status ?? "all",
    windowPart,
    includeOpen,
  ].join("|");
}

function TradeLog() {
  useTradeDataFreshMountInvalidation();
  const location = useLocation();
  const history = useHistory();
  const { user } = useUser();
  const { timezone: notificationTimezone, revision: notificationTzRevision } =
    useUserNotificationTimezone();
  const reportingTimeZone =
    notificationTimezone ??
    Intl.DateTimeFormat().resolvedOptions().timeZone ??
    "UTC";
  /** Prefer first load after Display Preferences TZ resolves (avoids device→API double fetch). */
  const reportingTimezoneReady = notificationTzRevision > 0;

  const {
    activeAccountCode,
    activeBrokerName,
    accounts: tradeAccounts,
    loading: tradeAccountsLoading,
    setActiveTradeAccount,
  } = useActiveBrokerAccount();
  const brokerAccountCode = activeAccountCode;
  const accountCodeLoaded = !tradeAccountsLoading;

  const [activeTab, setActiveTab] = useState<TradeLogTab>("log");
  const [trades, setTrades] = useState<Trade[]>([]);
  const signalsContext = useSignalsContextOptional();
  const ensureSignalsStatus = signalsContext?.ensureSignalsStatus;
  const replaceWithOpenTrades = signalsContext?.replaceWithOpenTrades;
  useLoadActiveBrokerSnapshot({ refresh: true });
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detailsTradeId, setDetailsTradeId] = useState<string | null>(null);
  const detailsTradeSnapshotRef = useRef<Trade | null>(null);
  const inlinePanelRef = useRef<TradeDetailsInlinePanelHandle | null>(null);
  const [isLogModalOpen, setIsLogModalOpen] = useState(false);
  const [logAttachmentStorageId, setLogAttachmentStorageId] = useState(() => `pending-${safeRandomUUID()}`);
  const [prefilledTradeData, setPrefilledTradeData] = useState<Partial<TradeFormData> | undefined>(undefined);
  const [isTemplateManagementModalOpen, setIsTemplateManagementModalOpen] = useState(false);
  
  // Check for autoSave and prefill query parameters on mount and when location changes
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const autoSave = searchParams.get("autoSave") === "true";
    const openModal = searchParams.get("openModal") === "true";
    const prefillData = searchParams.get("prefill");
    const tradeId = searchParams.get("tradeId");
    const tab = (searchParams.get("tab") || searchParams.get("view") || "").toLowerCase();

    // Deep link: open Trade Review tab.
    // Supported: /trade-log?tab=review  OR  /trade-log?view=trade-review
    if (tab === "review" || tab === "trade-review" || tab === "tradereview") {
      if (activeTab !== "review") setActiveTab("review");
    }
    
    // Handle tradeId query parameter to open trade details
    if (tradeId) {
      if (trades.length > 0) {
        const trade = trades.find(t => t.id === tradeId);
        if (trade) {
          detailsTradeSnapshotRef.current = trade;
          setActiveTab("log");
          setDetailsTradeId(trade.id);
          // Remove the tradeId parameter from URL
          const newSearchParams = new URLSearchParams(location.search);
          newSearchParams.delete("tradeId");
          const newSearch = newSearchParams.toString();
          history.replace({
            pathname: location.pathname,
            search: newSearch ? `?${newSearch}` : "",
          });
        }
      }
      return; // Don't process other query params if tradeId is present
    }
    
    // Auto-save trade directly without modal (from Tradar)
    if (autoSave && prefillData && user?.id) {
      const handleAutoSave = async () => {
        try {
          const decodedData = decodeURIComponent(prefillData);
          const parsedData = JSON.parse(decodedData) as TradeFormData;
          
          // Convert to Trade and save directly
          const newTrade = formDataToTrade(parsedData, user.id);
          const backendTrade = frontendTradeToBackendTrade(newTrade);
          await createTrade(backendTrade);
          // Reload trades from backend to get the created trade with backend ID
          await loadTradesFromBackend(undefined, undefined, { force: true });
          
          // No toast here - Tradar already shows one
          // Remove the query parameters from URL to clean it up
          history.replace(location.pathname);
        } catch (error) {
          console.error("Error parsing prefill data:", error);
          brokerSonnerToast({
            title: "Error",
            description: "Failed to log trade. Please try again.",
            status: "error",
            duration: 3000,
          });
          history.replace(location.pathname);
        }
      };
      handleAutoSave();
    }
    // Open modal with pre-filled data (manual logging with prefill)
    else if (openModal) {
      setLogAttachmentStorageId(`pending-${safeRandomUUID()}`);
      setIsLogModalOpen(true);
      
      // If prefill data is provided, parse and set it
      if (prefillData) {
        try {
          const decodedData = decodeURIComponent(prefillData);
          const parsedData = JSON.parse(decodedData) as Partial<TradeFormData>;
          setPrefilledTradeData(parsedData);
        } catch (error) {
          console.error("Error parsing prefill data:", error);
          brokerSonnerToast({
            title: "Error",
            description: "Failed to parse trade data. Please try again.",
            status: "error",
            duration: 3000,
          });
        }
      } else {
        // Clear prefilled data if no prefill parameter
        setPrefilledTradeData(undefined);
      }
      
      // Remove the query parameters from URL to clean it up
      history.replace(location.pathname);
    }
  }, [location, history, user, trades]);
  
  // Filter state
  const [filters, setFilters] = useState<TradeFilters>(() => {
    const dateRange = dateRangeForTradeLogFromPersisted(getInitialDashboardDateWindow());
    return {
      status: "all",
      type: "all",
      profitLoss: "all",
      symbols: [],
      searchQuery: "",
      dateRange,
    };
  });
  const [selectedPresetId, setSelectedPresetId] =
    useState<DashboardDatePresetTimeframe | null>(() =>
      preferredPresetFromPersisted(getInitialDashboardDateWindow())
    );

  /** Notification / email deep link: day range + account on Trade Log / Trade Review. */
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const localStart = (searchParams.get("local_start_date") || "").trim();
    const localEnd = (searchParams.get("local_end_date") || "").trim();
    const startMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(localStart);
    const endMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(localEnd);
    if (startMatch && endMatch) {
      const startDate = new Date(
        Number(startMatch[1]),
        Number(startMatch[2]) - 1,
        Number(startMatch[3]),
        0,
        0,
        0,
        0
      );
      const endDate = new Date(
        Number(endMatch[1]),
        Number(endMatch[2]) - 1,
        Number(endMatch[3]),
        0,
        0,
        0,
        0
      );
      if (!Number.isNaN(startDate.getTime()) && !Number.isNaN(endDate.getTime())) {
        setFilters((prev) => {
          const nextRange = { startDate, endDate };
          if (equalDateRange(prev.dateRange, nextRange)) return prev;
          return { ...prev, dateRange: nextRange };
        });
        setSelectedPresetId(null);
      }
    }

    const brokerCode =
      (searchParams.get("broker_account_code") || searchParams.get("account_code") || "").trim();
    if (brokerCode && tradeAccounts.length > 0) {
      const acc = tradeAccounts.find((a) => a.accountCode === brokerCode);
      if (
        acc &&
        (acc.accountCode !== activeAccountCode || acc.brokerName !== activeBrokerName)
      ) {
        void setActiveTradeAccount(acc.brokerName, acc.accountCode);
      }
    }
  }, [
    activeAccountCode,
    activeBrokerName,
    location.search,
    setActiveTradeAccount,
    tradeAccounts,
  ]);

  /** Latest load wins; avoids discarding fetch results when accountCode state lags override (switch/resync). */
  const tradesLoadSeqRef = useRef(0);
  /** Skip settle/remount refetches when account + window + status are unchanged. */
  const lastTradesListQueryKeyRef = useRef<string | null>(null);
  const inFlightTradesListQueryKeyRef = useRef<string | null>(null);

  useEffect(() => {
    setTrades([]);
    setIsLoading(true);
    lastTradesListQueryKeyRef.current = null;
  }, [brokerAccountCode, activeBrokerName]);

  // Filter trades based on current filters
  const filteredTrades = useMemo(() => {
    let filtered = trades.filter(
      (trade) => !tradeConflictsWithBrokerAccount(trade, brokerAccountCode)
    );

    // Filter by status
    if (filters.status && filters.status !== "all") {
      filtered = filtered.filter((trade) => trade.status === filters.status);
    }

    // Filter by symbols (client-side only — exact trade labels from loaded rows)
    if (filters.symbols && filters.symbols.length > 0) {
      const allowed = new Set(filters.symbols.map((sym) => sym.trim()));
      filtered = filtered.filter((trade) =>
        allowed.has(trade.symbol?.trim() ?? "")
      );
    }

    // Filter by type
    if (filters.type && filters.type !== "all") {
      filtered = filtered.filter((trade) => trade.type === filters.type);
    }

    // Filter by profit/loss
    if (filters.profitLoss && filters.profitLoss !== "all") {
      filtered = filtered.filter((trade) => {
        if (trade.status === "open") return false;
        if (filters.profitLoss === "winning") {
          return (trade.netPnL || 0) > 0;
        }
        if (filters.profitLoss === "losing") {
          return (trade.netPnL || 0) < 0;
        }
        return true;
      });
    }

    return filtered;
  }, [trades, filters, brokerAccountCode]);

  // Reuse live floating PnL from Tradar's shared SignalsContext (same source as Active Setups).
  const tableTrades = useMemo(() => {
    const takenTrades = signalsContext?.takenTrades ?? {};
    return filteredTrades.map((trade) => {
      if (trade.status !== "open") return trade;
      const live = takenTrades[trade.id]?.floatingPnl;
      return {
        ...trade,
        floatingPnl: typeof live === "number" ? live : undefined,
      } as Trade & { floatingPnl?: number };
    });
  }, [filteredTrades, signalsContext?.takenTrades]);

  // Trade Log should also activate signals status so shared WebSocket can stream open-trade FPL updates.
  useEffect(() => {
    if (!ensureSignalsStatus) return;
    ensureSignalsStatus();
  }, [ensureSignalsStatus]);

  // Seed open trades from DB into shared context so account_update.open_trades_unrealized
  // can merge floating PnL per trade (same path used by Tradar active setups).
  useEffect(() => {
    if (!replaceWithOpenTrades) return;
    const openTradesFromDb = trades.filter((trade) => trade.status === "open");
    replaceWithOpenTrades(openTradesFromDb);
  }, [trades, replaceWithOpenTrades]);

  // Statistics for header mini-cards come from useTradeStatistics (see statsFilters).

  const tradesUpdatedRetryTimeoutsRef = useRef<number[]>([]);

  // Preset labels aligned to local calendar (see deriveTimeframeKeyFromLocalDateRange)
  const timeframe = useMemo(
    () => deriveTimeframeKeyFromLocalDateRange(filters.dateRange),
    [filters.dateRange]
  );

  useEffect(() => {
    writeDashboardDateWindow(
      persistStateFromTradeLog(filters.dateRange, timeframe, selectedPresetId)
    );
  }, [filters.dateRange, timeframe, selectedPresetId]);

  useDashboardDateWindowListener((p) => {
    const dr = dateRangeForTradeLogFromPersisted(p);
    setFilters((f) => (equalDateRange(f.dateRange, dr) ? f : { ...f, dateRange: dr }));
    setSelectedPresetId(preferredPresetFromPersisted(p));
  });

  const tradeReviewListWindow = useMemo(
    () =>
      resolveTradeListWindowQuery(
        filters.dateRange || { startDate: null, endDate: null },
        timeframe,
        reportingTimeZone,
        { includeTimezoneQuery: true }
      ),
    [filters.dateRange, timeframe, reportingTimeZone]
  );

  const statsFilters: TradeStatisticsFilters = useMemo(() => {
    const apiFilters: TradeStatisticsFilters = {};

    const window = tradeReviewListWindow;
    if (window.kind === "local") {
      apiFilters.local_start_date = window.period.local_start_date;
      apiFilters.local_end_date = window.period.local_end_date;
      if (window.period.timezone) {
        apiFilters.timezone = window.period.timezone;
      }
    } else {
      apiFilters.timeframe = window.timeframe;
    }
    apiFilters.timezone = apiFilters.timezone ?? reportingTimeZone;

    // Status filter
    if (filters.status && filters.status !== "all") {
      apiFilters.status = filters.status;
    }
    
    // Symbol filter (if single symbol selected)
    if (filters.symbols && filters.symbols.length === 1) {
      apiFilters.symbol = filters.symbols[0];
    }

    if (filters.type === "buy" || filters.type === "sell") {
      apiFilters.trade_type = filters.type;
    }

    if (filters.profitLoss === "winning" || filters.profitLoss === "losing") {
      apiFilters.profit_loss = filters.profitLoss;
    }
    
    // Broker account code (include if available - backend has fallback if not provided)
    // Note: We include account code if available, but don't wait for it to fire the request
    // This allows parallel execution - statistics fires immediately, account code added if available
    if (brokerAccountCode) {
      apiFilters.broker_account_code = brokerAccountCode;
    }
    if (activeBrokerName) {
      apiFilters.broker_name = activeBrokerName;
    }
    
    // Use fields=basic for Trade Page to optimize performance (3-4x faster)
    // This skips comparisons, detailed metrics, and portfolio calculations
    apiFilters.fields = "basic";
    
    return apiFilters;
  }, [
    timeframe,
    filters.dateRange,
    filters.status,
    filters.symbols,
    filters.type,
    filters.profitLoss,
    brokerAccountCode,
    activeBrokerName,
    reportingTimeZone,
    notificationTzRevision,
    tradeReviewListWindow,
  ]);

  /** Distinct trade labels from loaded rows — no instrument-setup or stats synthesis. */
  const availableSymbols = useMemo(() => {
    const fromTrades = new Set<string>();
    trades.forEach((trade) => {
      const sym = trade.symbol?.trim();
      if (sym) fromTrades.add(sym);
    });
    return Array.from(fromTrades).sort((a, b) => a.localeCompare(b));
  }, [trades]);

  // Fetch trades with selected timeframe (or custom start_date/end_date). Refetches when date range changes.
  // When overrideAccountCode is provided (e.g. on account switch), use it so we don't race with state updates.
  // Without `force`, skips when the same list query is already loaded or in flight (account/TZ settle).
  const loadTradesFromBackend = useCallback(async (
    overrideAccountCode?: string | null,
    overrideBrokerName?: string | null,
    options?: { force?: boolean }
  ) => {
    if (!user?.id) {
      tradesLoadSeqRef.current += 1;
      lastTradesListQueryKeyRef.current = null;
      inFlightTradesListQueryKeyRef.current = null;
      setTrades([]);
      setIsLoading(false);
      setHasLoadedOnce(true);
      return;
    }

    const accountToUse = overrideAccountCode !== undefined ? overrideAccountCode : brokerAccountCode;
    const brokerToUse =
      overrideBrokerName !== undefined ? overrideBrokerName : activeBrokerName;
    const queryKey = buildTradeLogListQueryKey({
      userId: user.id,
      accountCode: accountToUse,
      brokerName: brokerToUse,
      dateRange: filters.dateRange,
      status: filters.status,
      timeframe,
      reportingTimeZone,
    });
    const force = options?.force === true;

    if (
      !force &&
      (inFlightTradesListQueryKeyRef.current === queryKey ||
        lastTradesListQueryKeyRef.current === queryKey)
    ) {
      return;
    }

    const seq = ++tradesLoadSeqRef.current;
    inFlightTradesListQueryKeyRef.current = queryKey;
    try {
      setIsLoading(true);
      setError(null);
      const opts: Parameters<typeof fetchTrades>[0] = {};
      if (accountToUse) opts.broker_account_code = accountToUse;
      if (brokerToUse) opts.broker_name = brokerToUse;
      // Force fresh list fetches (important immediately after broker resync).
      opts.cache_bust = Date.now();

      // Keep /trades aligned with /trades/statistics (local_* + timezone when applicable).
      const win = resolveTradeListWindowQuery(
        filters.dateRange || { startDate: null, endDate: null },
        timeframe,
        reportingTimeZone,
        { includeTimezoneQuery: true }
      );
      if (win.kind === "local") {
        opts.local_period = win.period;
      } else {
        opts.timeframe = win.timeframe;
      }

      const mapTradesResponse = (
        response: Awaited<ReturnType<typeof fetchTrades>>
      ): Trade[] => (response?.trades || []).map(backendTradeToFrontendTrade);

      let frontendTrades = mapTradesResponse(await fetchTrades(opts));

      // Open positions are not exit-date cohort — always merge for the active account.
      if (
        accountToUse &&
        (!filters.status || filters.status === "all" || filters.status === "open")
      ) {
        try {
          const openResponse = await fetchTrades({
            broker_account_code: accountToUse,
            broker_name: brokerToUse || undefined,
            status: "open",
            limit: 500,
            cache_bust: opts.cache_bust,
          });
          const openRows = mapTradesResponse(openResponse);
          if (openRows.length > 0) {
            const byId = new Map(frontendTrades.map((trade) => [trade.id, trade]));
            for (const trade of openRows) {
              byId.set(trade.id, trade);
            }
            frontendTrades = Array.from(byId.values());
          }
        } catch (openErr) {
          console.warn("Could not load open trades for account:", openErr);
        }
      }

      if (seq !== tradesLoadSeqRef.current) return;
      lastTradesListQueryKeyRef.current = queryKey;
      setTrades(frontendTrades);
    } catch (err) {
      console.error("Error loading trades from backend:", err);
      if (seq !== tradesLoadSeqRef.current) return;
      setError(err instanceof Error ? err.message : "Failed to load trades");
      setTrades([]);
    } finally {
      if (inFlightTradesListQueryKeyRef.current === queryKey) {
        inFlightTradesListQueryKeyRef.current = null;
      }
      if (seq !== tradesLoadSeqRef.current) return;
      setIsLoading(false);
      setHasLoadedOnce(true);
    }
  }, [
    user?.id,
    brokerAccountCode,
    activeBrokerName,
    filters.dateRange,
    filters.status,
    timeframe,
    reportingTimeZone,
  ]);

  // Load once credentials + reporting TZ are ready; skip when query key is unchanged.
  useEffect(() => {
    if (user?.id && accountCodeLoaded && reportingTimezoneReady) {
      void loadTradesFromBackend();
    }
  }, [user?.id, accountCodeLoaded, reportingTimezoneReady, loadTradesFromBackend]);

  // Listen for account switch and resync – refetch with the correct account so we don't show wrong data.
  useEffect(() => {
    const handleTradesUpdated = async (event: Event) => {
      const detail = (event as CustomEvent<{ action?: string; accountCode?: string; brokerName?: string }>)
        ?.detail;
      if (detail?.action === "marketDataPreference") return;
      if (isDestructiveTradeDataAction(detail?.action)) {
        clearAllTradeCaches();
      } else {
        invalidateTradeDataCaches();
      }
      // Clear pending retries from prior sync events to avoid stale overlaps.
      tradesUpdatedRetryTimeoutsRef.current.forEach((id) => window.clearTimeout(id));
      tradesUpdatedRetryTimeoutsRef.current = [];

      const switchedAccount = detail?.accountCode;
      const switchedBroker = detail?.brokerName;
      if (switchedAccount) {
        void loadTradesFromBackend(switchedAccount, switchedBroker ?? null, { force: true });
        tradesUpdatedRetryTimeoutsRef.current.push(
          window.setTimeout(
            () =>
              void loadTradesFromBackend(switchedAccount, switchedBroker ?? null, {
                force: true,
              }),
            1500
          ),
          window.setTimeout(
            () =>
              void loadTradesFromBackend(switchedAccount, switchedBroker ?? null, {
                force: true,
              }),
            4000
          )
        );
        return;
      }
      void loadTradesFromBackend(undefined, undefined, { force: true });
      tradesUpdatedRetryTimeoutsRef.current.push(
        window.setTimeout(
          () => void loadTradesFromBackend(undefined, undefined, { force: true }),
          1500
        ),
        window.setTimeout(
          () => void loadTradesFromBackend(undefined, undefined, { force: true }),
          4000
        )
      );
    };
    window.addEventListener("tradesUpdated", handleTradesUpdated);
    return () => {
      window.removeEventListener("tradesUpdated", handleTradesUpdated);
      tradesUpdatedRetryTimeoutsRef.current.forEach((id) => window.clearTimeout(id));
      tradesUpdatedRetryTimeoutsRef.current = [];
    };
  }, [loadTradesFromBackend]);

  // Note: tradesUpdated is handled above (account switch + resync) with correct account for refetch.
  // Note: Statistics will fire once initially (without account code), then again when account code loads
  // This is acceptable - backend has fallback logic. To avoid duplicate, we could remove brokerAccountCode
  // from dependencies, but then statistics won't use account code even when available.
  // Alternative: Fire statistics in loadInitialData in parallel, but that requires hook refactoring.

  // Fetch statistics from backend API only
  const { statistics: backendStats, loading: statsLoading, refetch: refetchStats } = useTradeStatistics(statsFilters);
  
  // Backend statistics for TradeStatistics component
  const backendStatisticsForComponent = useMemo(() => {
    if (!backendStats) return null;
    return {
      current_win_streak: backendStats.current_win_streak,
      current_loss_streak: backendStats.current_loss_streak,
      longest_win_streak: backendStats.longest_win_streak,
      longest_loss_streak: backendStats.longest_loss_streak,
    };
  }, [backendStats]);

  // Mini-card statistics — backend by default; client-side when symbols are filtered.
  const statistics = useMemo(() => {
    const symbolFilterActive = (filters.symbols?.length ?? 0) > 0;
    if (symbolFilterActive) {
      return calculateTradeStatistics(filteredTrades);
    }

    if (!backendStats || statsLoading) {
      return {
        totalTrades: 0,
        openTrades: 0,
        closedTrades: 0,
        winningTrades: 0,
        losingTrades: 0,
        winRate: 0,
        totalPnL: 0,
        averageWin: 0,
        averageLoss: 0,
        largestWin: 0,
        largestLoss: 0,
        totalReturnPercent: 0,
        averageReturnPercent: 0,
      };
    }

    return {
      totalTrades: backendStats.total_trades,
      openTrades: backendStats.open_trades,
      closedTrades: backendStats.closed_trades,
      winningTrades: backendStats.winning_trades,
      losingTrades: backendStats.losing_trades,
      winRate: backendStats.win_rate,
      totalPnL: backendStats.total_pnl,
      averageWin: backendStats.average_win,
      averageLoss: backendStats.average_loss,
      largestWin: backendStats.largest_win,
      largestLoss: backendStats.largest_loss,
      totalReturnPercent: backendStats.total_return_percent,
      averageReturnPercent: backendStats.average_return_percent,
      mostTradedSymbol: backendStats.most_traded_symbol || undefined,
      mostTradedSymbolCount: backendStats.most_traded_symbol_count || undefined,
    };
  }, [backendStats, statsLoading, filters.symbols, filteredTrades]);


  const dismissExpandedTrade = useCallback((nextId: string | null = null) => {
    setDetailsTradeId(nextId);
    if (!nextId) {
      detailsTradeSnapshotRef.current = null;
    }
  }, []);

  const navigateExpandedTrade = useCallback(
    (trade: Trade) => {
      const closing = detailsTradeId === trade.id;
      if (closing) {
        dismissExpandedTrade(null);
        return;
      }
      detailsTradeSnapshotRef.current = trade;
      setActiveTab("log");
      setDetailsTradeId(trade.id);
    },
    [detailsTradeId, dismissExpandedTrade]
  );

  const requestExpandedTradeNavigation = useCallback(
    (action: () => void) => {
      if (detailsTradeId != null && inlinePanelRef.current?.isDirty()) {
        inlinePanelRef.current.requestNavigate(action);
        return;
      }
      action();
    },
    [detailsTradeId]
  );

  const handleJournalSaved = useCallback((updatedTrade: Trade) => {
    setTrades((prev) => prev.map((row) => (row.id === updatedTrade.id ? updatedTrade : row)));
    detailsTradeSnapshotRef.current = updatedTrade;
  }, []);

  const handleRowClick = useCallback(
    (trade: Trade) => {
      requestExpandedTradeNavigation(() => navigateExpandedTrade(trade));
    },
    [navigateExpandedTrade, requestExpandedTradeNavigation]
  );

  const handleExpandedRowDismiss = useCallback(() => {
    requestExpandedTradeNavigation(() => dismissExpandedTrade(null));
  }, [dismissExpandedTrade, requestExpandedTradeNavigation]);

  const openTradeFromReview = useCallback(
    (tradeId: string) => {
      const trade = trades.find((row) => row.id === tradeId);
      if (!trade) return;
      requestExpandedTradeNavigation(() => navigateExpandedTrade(trade));
    },
    [navigateExpandedTrade, requestExpandedTradeNavigation, trades]
  );

  useEffect(() => {
    if (!detailsTradeId) return;
    if (!tableTrades.some((row) => row.id === detailsTradeId)) {
      dismissExpandedTrade(null);
    }
  }, [tableTrades, detailsTradeId, dismissExpandedTrade]);

  const handleLogNewTrade = () => {
    setLogAttachmentStorageId(`pending-${safeRandomUUID()}`);
    setIsLogModalOpen(true);
  };

  const handleSaveTrade = async (formData: TradeFormData, attachments?: Attachment[]) => {
    if (!user?.id) {
      brokerSonnerToast({
        title: "Error",
        description: "User not authenticated. Please log in again.",
        status: "error",
        duration: 3000,
      });
      return;
    }

    try {
      setIsLoading(true);
      // Convert form data to Trade object
      const newTrade = formDataToTrade(formData, user.id);
      
      // Add trade to backend
      const backendTrade = frontendTradeToBackendTrade(newTrade);
      const created = await createTrade(backendTrade);
      await migrateAttachmentsTradeId(logAttachmentStorageId, created.id);

      // Reload trades from backend to get the created trade with backend ID
      await loadTradesFromBackend(undefined, undefined, { force: true });
      
      // Show success toast
      brokerSonnerToast({
        title: "Trade logged successfully",
        description: `${formData.symbol} trade has been logged.`,
        status: "success",
        duration: 3000,
      });
      
      setIsLogModalOpen(false);
      setPrefilledTradeData(undefined);
    } catch (error: any) {
      brokerSonnerToast({
        title: "Error",
        description: error.message || "Failed to save trade. Please try again.",
        status: "error",
        duration: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  /** Profile → Broker Management for the account currently in view. */
  const handleImport = () => {
    const activeRow =
      activeBrokerName && activeAccountCode
        ? tradeAccounts.find(
            (a) =>
              a.brokerName === activeBrokerName.toLowerCase() &&
              a.accountCode === activeAccountCode
          ) ?? null
        : null;
    history.push(
      buildProfileBrokerApiUrl({
        brokerName: activeBrokerName,
        accountCode: activeAccountCode,
        brokerTab: resolveProfileBrokerTabForImport({
          brokerSupportsApiSync: true,
          activeAccount: activeRow,
        }),
      })
    );
  };

  const handleExport = () => {
    try {
      if (filteredTrades.length === 0) {
        brokerSonnerToast({
          title: "No trades to export",
          description: "Please add some trades or adjust your filters.",
          status: "warning",
          duration: 3000,
        });
        return;
      }

      const filename = generateExportFilename();
      exportTradesToCSV(filteredTrades, filename, { displayTimeZone: reportingTimeZone });
      
      brokerSonnerToast({
        title: "Export successful",
        description: `${filteredTrades.length} trade(s) exported to ${filename}`,
        status: "success",
        duration: 5000,
      });
    } catch (error) {
      console.error("Export failed:", error);
      brokerSonnerToast({
        title: "Export failed",
        description: "An error occurred while exporting trades. Please try again.",
        status: "error",
        duration: 5000,
      });
    }
  };

  const handleBulkExport = (trades: Trade[]) => {
    try {
      if (trades.length === 0) {
        brokerSonnerToast({
          title: "No trades to export",
          description: "Please select some trades to export.",
          status: "warning",
          duration: 3000,
        });
        return;
      }

      const filename = generateExportFilename();
      exportTradesToCSV(trades, filename, { displayTimeZone: reportingTimeZone });
      
      brokerSonnerToast({
        title: "Export successful",
        description: `${trades.length} trade(s) exported to ${filename}`,
        status: "success",
        duration: 3000,
      });
    } catch (error) {
      console.error("Export failed:", error);
      brokerSonnerToast({
        title: "Export failed",
        description: "An error occurred while exporting trades. Please try again.",
        status: "error",
        duration: 5000,
      });
    }
  };

  // Render main content based on state
  const renderMainContent = (): React.ReactNode => {
    const emptyStateVariant: "no-trades" | "filter-mismatch" | null =
      tableTrades.length === 0 ? (trades.length === 0 ? "no-trades" : "filter-mismatch") : null;

    return (
      <TradesTable
        trades={tableTrades}
        emptyStateVariant={emptyStateVariant}
        onBulkExport={handleBulkExport}
        onRowClick={handleRowClick}
        expandedRowId={detailsTradeId}
        onExpandedRowDismiss={handleExpandedRowDismiss}
        renderExpandedRow={(trade) => (
          <TradeDetailsInlinePanel
            ref={inlinePanelRef}
            trade={trade}
            onJournalSaved={handleJournalSaved}
          />
        )}
        filters={filters}
        onFiltersChange={setFilters}
        onLogNewTrade={handleLogNewTrade}
        onExport={handleExport}
        onImport={handleImport}
        onManageTemplates={() => setIsTemplateManagementModalOpen(true)}
        availableSymbols={availableSymbols}
        enableHorizontalScrollOnMobile
      />
    );
  };

  const shadcnInset = useShadcnInsetChrome();
  const pageMinHeight = statisticsPageMinHeight(shadcnInset);
  const isInitialLoading =
    !hasLoadedOnce && (isLoading || statsLoading || user?.id !== undefined);
  const showLoadingMessage =
    isInitialLoading ||
    (isLoading && trades.length === 0 && accountCodeLoaded);
  const hasData = hasLoadedOnce && !showLoadingMessage;

  const tradeLogPageFlexProps: any = {
    direction: "column",
    w: "100%",
    minH: pageMinHeight,
    pt: shadcnInset ? 0 : { base: "100px", md: "80px", lg: "40px" },
    pb: shadcnInset ? 0 : { base: "100px", md: "80px", lg: "40px" },
  };

  return (
    <ErrorBoundary>
      <FlexAny {...tradeLogPageFlexProps} data-trade-log-page="">
        {showLoadingMessage ? (
          <FlexAny flex="1" minH="0" w="100%" align="center" justify="center">
            <StatisticsEmptyState
              variant="loading"
              fillParent
              title="Loading trades"
              description="Please wait while we load your trades."
            />
          </FlexAny>
        ) : null}

        {hasData ? (
          <>
            <TradeLogHeaderAny
              statistics={statistics}
              backendStats={backendStats}
              trades={filteredTrades}
              dateRange={filters.dateRange || { startDate: null, endDate: null }}
              selectedPresetId={selectedPresetId}
              onDateRangeChange={(dateRange: DateRange, options?: DashboardDateRangeChangeOptions) => {
                setFilters((f) => ({ ...f, dateRange }));
                if (options?.presetId) setSelectedPresetId(options.presetId);
                else setSelectedPresetId(null);
              }}
              onExport={handleExport}
              onImport={handleImport}
            />

            {/* Error Alert */}
            {error && (
              <AlertAny status="error" mb={4} borderRadius="md">
                <AlertIcon />
                <Box flex="1">
                  <AlertTitleAny>Error loading trades</AlertTitleAny>
                  <AlertDescriptionAny>{error}</AlertDescriptionAny>
                </Box>
                <ButtonAny
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setError(null);
                    void loadTradesFromBackend(undefined, undefined, { force: true });
                  }}
                >
                  Retry
                </ButtonAny>
              </AlertAny>
            )}

            <Tabs
              data-trade-log-tabs=""
              value={activeTab}
              onValueChange={(value) => setActiveTab(value as TradeLogTab)}
            >
              <TabsList className="!transition-none">
                <TabsTrigger
                  value="log"
                  className="!transition-none !shadow-none after:!opacity-0 after:!transition-none data-active:!border-border data-active:!bg-[var(--trade-log-tab-active-bg)] data-active:!text-[var(--brand-tropical-surf)] data-[state=active]:!border-border data-[state=active]:!bg-[var(--trade-log-tab-active-bg)] data-[state=active]:!text-[var(--brand-tropical-surf)] dark:data-active:!border-border dark:data-active:!bg-[var(--trade-log-tab-active-bg)] dark:data-[state=active]:!border-border dark:data-[state=active]:!bg-[var(--trade-log-tab-active-bg)]"
                >
                  Trade Log
                </TabsTrigger>
                <TabsTrigger
                  value="review"
                  className="!transition-none !shadow-none after:!opacity-0 after:!transition-none data-active:!border-border data-active:!bg-[var(--trade-log-tab-active-bg)] data-active:!text-[var(--brand-tropical-surf)] data-[state=active]:!border-border data-[state=active]:!bg-[var(--trade-log-tab-active-bg)] data-[state=active]:!text-[var(--brand-tropical-surf)] dark:data-active:!border-border dark:data-active:!bg-[var(--trade-log-tab-active-bg)] dark:data-[state=active]:!border-border dark:data-[state=active]:!bg-[var(--trade-log-tab-active-bg)]"
                >
                  Trade Review
                </TabsTrigger>
              </TabsList>

              <TabsContent value="log" className="mt-0">
                {renderMainContent()}
              </TabsContent>
              <TabsContent value="review">
                <InstrumentSetupGate featureLabel="Trade Review">
                  <TradeReviewTab
                  brokerAccountCode={brokerAccountCode}
                  tradesForSymbols={trades}
                  tradeListWindow={tradeReviewListWindow}
                  reportingTimeZoneOverride={reportingTimeZone}
                  cohortIntent="trade_log"
                  onOpenTrade={openTradeFromReview}
                />
                </InstrumentSetupGate>
              </TabsContent>
            </Tabs>
          </>
        ) : null}

      {/* Log Trade Modal */}
      <LogTradeModal
        isOpen={isLogModalOpen}
        onClose={() => {
          if (logAttachmentStorageId.startsWith("pending-")) {
            void deleteAttachmentsForTrade(logAttachmentStorageId);
          }
          setIsLogModalOpen(false);
          setPrefilledTradeData(undefined); // Clear prefilled data when modal closes
        }}
        attachmentStorageId={logAttachmentStorageId}
        onSave={handleSaveTrade}
        initialData={prefilledTradeData}
        isEditMode={false}
      />

      <TemplateManagementModal
        isOpen={isTemplateManagementModalOpen}
        onClose={() => setIsTemplateManagementModalOpen(false)}
        onSelectTemplate={(template) => {
          // Open log trade modal with template data
          setIsTemplateManagementModalOpen(false);
          setLogAttachmentStorageId(`pending-${safeRandomUUID()}`);
          setIsLogModalOpen(true);
          // The LogTradeModal will handle template loading via the template dropdown
        }}
      />
      </FlexAny>
    </ErrorBoundary>
  );
}

export default TradeLog;

